import React, { useState, useEffect } from 'react';
import { firestore } from '../config/firebaseConfig';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import './ProfessoresForm.css';

function ProfessoresForm({ professor, onCancel }) {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    areaAtuacao: '',
    linkedin: '',
    fotoUrl: '',
    miniBio: '',
    cursosIds: '',
    dataCriacao: '',
    dataAtual: '',
    status: 'Ativo', // Definindo o status como 'Ativo' por padrão
  });
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (professor) {
      setFormData(professor);
    }
  }, [professor]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setError(''); // Limpar erro ao alterar o valor
  };

  const handleNext = () => {
    const currentStep = steps[step];
    if (currentStep.inputProps.required && !formData[currentStep.inputProps.name]) {
      setError(`Por favor, preencha o campo "${currentStep.label}".`);
      return;
    }

    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    const allRequiredFilled = steps.every(step => !step.inputProps.required || formData[step.inputProps.name]);
    if (!allRequiredFilled) {
      setError('Ainda há campos obrigatórios não preenchidos. Por favor, complete todos os campos obrigatórios antes de prosseguir.');
      return;
    }

    const timestamp = new Date().toISOString();
    const updatedData = {
      ...formData,
      dataAtual: timestamp,
    };

    try {
      if (professor?.profId) {
        await updateDoc(doc(firestore, 'professores', professor.profId), updatedData);
      } else {
        await setDoc(doc(firestore, 'professores', new Date().getTime().toString()), {
          ...updatedData,
          dataCriacao: timestamp
        });
      }
      setFormData({
        nome: '',
        email: '',
        telefone: '',
        areaAtuacao: '',
        linkedin: '',
        fotoUrl: '',
        miniBio: '',
        cursosIds: '',
        dataCriacao: '',
        dataAtual: '',
        status: 'Ativo', // Resetando o status para 'Ativo' após o envio do formulário
      });
      setSuccess('Ótimo trabalho! Seus dados foram salvos com sucesso.');
      setTimeout(() => onCancel(), 3000); // Atraso para mostrar a mensagem de sucesso
    } catch (error) {
      console.error('Erro ao salvar professor:', error);
      setError('Houve um problema ao salvar os dados. Por favor, tente novamente mais tarde.');
    }
  };

  const steps = [
    { label: 'Nome', inputProps: { name: 'nome', type: 'text', value: formData.nome, required: true } },
    { label: 'E-mail', inputProps: { name: 'email', type: 'email', value: formData.email, required: true } },
    { label: 'Telefone', inputProps: { name: 'telefone', type: 'tel', value: formData.telefone, required: true } },
    { label: 'Área de Atuação', inputProps: { name: 'areaAtuacao', type: 'text', value: formData.areaAtuacao } },
    { label: 'LinkedIn', inputProps: { name: 'linkedin', type: 'url', value: formData.linkedin } },
    { label: 'Foto URL', inputProps: { name: 'fotoUrl', type: 'url', value: formData.fotoUrl } },
    { label: 'Mini Bio', inputProps: { name: 'miniBio', type: 'text', value: formData.miniBio } },
    // O campo 'status' não está incluído nos steps, pois não é solicitado no cadastro
  ];

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Evitar comportamento padrão de submit ao pressionar Enter
      handleNext();
    }
  };

  const currentStep = steps[step];
  const progressPercentage = ((step + 1) / steps.length) * 100;
  const isContinueEnabled = !currentStep.inputProps.required || (formData[currentStep.inputProps.name] && formData[currentStep.inputProps.name].trim() !== '');

  const getInstructionMessage = () => {
    if (step === 0) return "Vamos começar! Por favor, preencha o nome completo.";
    if (step === 1) return "Digite o e-mail para contato.";
    if (step === 2) return "Informe o número de telefone.";
    if (step === 3) return "Descreva sua área de atuação.";
    if (step === 4) return "Adicione o link do seu perfil no LinkedIn, se tiver.";
    if (step === 5) return "Adicione a URL da sua foto.";
    if (step === 6) return "Escreva uma breve biografia.";
    return "Preencha os campos e avance para concluir o cadastro.";
  };

  return (
    <div className="professor-form-container">
      <div className="instruction-message">
        {getInstructionMessage()}
      </div>
      <div className="progress-bar">
        <div className="progress" style={{ width: `${progressPercentage}%` }}></div>
      </div>
      <form className="professor-form" onSubmit={(e) => e.preventDefault()}>
        <div className="form-group">
          <label htmlFor={currentStep.inputProps.name}>{currentStep.label}</label>
          {currentStep.inputProps.type === 'select' ? (
            <select
              id={currentStep.inputProps.name}
              name={currentStep.inputProps.name}
              value={currentStep.inputProps.value}
              onChange={handleChange}
            >
              <option value="">Selecione</option>
              {currentStep.inputProps.options.map(option => (
                <option key={option} value={option.toLowerCase()}>{option}</option>
              ))}
            </select>
          ) : (
            <input
              id={currentStep.inputProps.name}
              name={currentStep.inputProps.name}
              type={currentStep.inputProps.type}
              value={currentStep.inputProps.value}
              onChange={handleChange}
              onKeyDown={handleKeyDown} // Adicione este manipulador
              required={currentStep.inputProps.required}
            />
          )}
        </div>
        {error && <div className="form-feedback form-error">{error}</div>}
        {success && <div className="form-feedback form-success">{success}</div>}
        <div className="form-actions">
          <button type="button" onClick={handlePrevious} disabled={step === 0}>Voltar</button>
          <button type="button" onClick={onCancel}>Cancelar</button>
          {!currentStep.inputProps.required && step < steps.length - 1 && (
            <button type="button" onClick={handleNext}>Pular</button>
          )}
          {step < steps.length - 1 && (
            <button type="button" onClick={handleNext} disabled={!isContinueEnabled}>Continuar</button>
          )}
          {step === steps.length - 1 && (
            <button type="button" onClick={handleSubmit}>Concluir</button>
          )}
        </div>
      </form>
    </div>
  );
}

export default ProfessoresForm;
